<template>
  <div class="chat-container" :class="{ 'user-container': isUser }">
    <div v-if="!isUser" class="bot-circle">R</div>

    <div class="chat-bubbles">
      <div
        :class="[
          'chat-bubble',
          isUser
            ? 'user-message'
            : canBeCopied
            ? 'copyable-message'
            : 'bot-message',
        ]"
        style="display: flex; flex-direction: column; padding-left: 25px"
      >
        <div v-if="canBeCopied">
          <span v-if="options.length === 0" class="copyable-content">
            <span v-html="formattedText"></span>

            <b-icon
              :title="canBeCopied ? 'Click to copy' : ''"
              icon="content-copy"
              class="copy-icon"
              @click.native="copyToClipboard(text)"
            />
          </span>

          <span v-if="options.length" class="copyable-content-multiple">
            <span v-html="formattedText"></span>

            <span
              v-for="(option, index) in options"
              :key="index"
              class="copyable-options"
            >
              <span>{{ option }}</span>
              <b-icon
                :title="canBeCopied ? 'Click to copy' : ''"
                icon="content-copy"
                class="copy-icon"
                @click.native="copyToClipboard(option)"
              />
            </span>
          </span>
        </div>

        <span v-else v-html="formattedText"></span>
      </div>

      <div
        v-if="hasDocuments"
        :class="[
          'chat-bubble',
          isUser ? 'user-message' : 'bot-message',
          'documents-bubble',
        ]"
      >
        <strong>Documents:</strong>
        <ul class="documents-list">
          <li v-for="(doc, idx) in normalizedDocuments" :key="docKey(doc, idx)">
            <a
              v-if="doc.presigned_url"
              class="document-link"
              :href="doc.presigned_url"
              :download="doc.title || 'document'"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ doc.title || 'Untitled document' }}
            </a>
            <span v-else>{{ doc.title || 'Untitled document' }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import MarkdownIt from 'markdown-it';
// eslint-disable-next-line import/no-extraneous-dependencies
import DOMPurify from 'dompurify';

const md = new MarkdownIt({
  breaks: true, // supports line breaks with newlines
  html: false, // disable raw HTML in input
  linkify: true,
});

// Remember old renderer, if overridden, or proxy to default renderer
const defaultRender =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  // If you are sure other plugins don't add `target` - dropped check
  const aIndex = tokens[idx].attrIndex('target');

  if (aIndex < 0) {
    tokens[idx].attrPush(['target', '_blank']); // add new attribute
  } else {
    tokens[idx].attrs[aIndex][1] = '_blank'; // replace value of existing attr
  }

  // Add rel="noopener noreferrer" for security
  const relIndex = tokens[idx].attrIndex('rel');
  if (relIndex < 0) {
    tokens[idx].attrPush(['rel', 'noopener noreferrer']);
  } else {
    tokens[idx].attrs[relIndex][1] = 'noopener noreferrer';
  }

  return defaultRender(tokens, idx, options, env, self);
};

export default {
  props: {
    text: {
      type: String,
      required: true,
    },
    documents: {
      type: Array,
      default: () => [],
      required: false,
    },
    isUser: {
      type: Boolean,
      required: true,
    },
    canBeCopied: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
      required: false,
    },
  },
  computed: {
    hasDocuments() {
      return Array.isArray(this.documents) && this.documents.length > 0;
    },
    normalizedDocuments() {
      return (this.documents || []).map((d) => ({
        title: d?.Title ?? d?.title ?? '',
        presigned_url: d?.presigned_url ?? d?.presignedUrl ?? d?.url ?? '',
      }));
    },
    formattedText() {
      let dirtyHtml = md.render(this.text);

      if (this.text.includes('\n')) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = dirtyHtml;

        const paragraphs = wrapper.querySelectorAll('p');
        for (let i = 1; i < paragraphs.length; i++) {
          const prev = paragraphs[i - 1];
          const curr = paragraphs[i];

          if (!prev.closest('li') && !curr.closest('li')) {
            const br = document.createElement('br');
            curr.parentNode.insertBefore(br, curr);
          }
        }

        dirtyHtml = wrapper.innerHTML;
      }

      return DOMPurify.sanitize(dirtyHtml, { ADD_ATTR: ['target'] });
    },
  },
  methods: {
    docKey(doc, idx) {
      return (
        doc?.id ||
        doc?.key ||
        doc?.presigned_url ||
        `${doc?.title || 'doc'}-${idx}`
      );
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$buefy.toast.open({
            duration: 5000,
            message: 'Message copied to the clipboard!',
            type: 'is-info',
          });
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
        });
    },
  },
};
</script>

<style scoped>
.copyable-options {
  display: flex;
  flex-direction: row;
}

.chat-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
}

.user-container {
  flex-direction: row-reverse;
  width: 85%;
}

.bot-circle {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  background-color: #5a67d8;
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  border-radius: 50%;
  margin-right: 10px;
}

.user-container .bot-circle {
  margin-right: 0;
  margin-left: 10px;
}

.chat-bubbles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.chat-bubble {
  max-width: 85%;
  padding: 10px 15px;
  border-radius: 0 15px 15px 15px;
  word-break: break-word;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.chat-bubble:hover {
  background-color: #cfdcff;
}

.user-message {
  align-self: flex-end;
  background-color: #e6e8ff;
  border-radius: 15px 15px 0 15px;
  color: #333;
}

.bot-message {
  align-self: flex-start;
  background-color: #dfe9ff;
  color: #333;
}

.copyable-message {
  align-self: flex-start;
  background-color: #fff6e59b;
  border: 1px solid #ffe4b5;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyable-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.copyable-content-multiple {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.copy-icon {
  margin-left: auto;
  font-size: 16px;
  color: #ffa500;
}

.documents-bubble {
  cursor: default;
  background-color: #f6f8fa;
  border: 1px solid #e1e4e8;
}

.documents-list {
  margin: 6px 0 0;
  padding-left: 18px;
  list-style-type: disc;
  list-style-position: outside;
}

.documents-list li {
  display: list-item;
}

.document-link {
  text-decoration: underline;
}
</style>
